import React, { useState, useEffect, useCallback, useRef } from 'react';
import './styles/game.css';
import './styles/animations.css';

// Components
import TopBar from './components/TopBar';
import FloatingMenu from './components/FloatingMenu';
import MissionSelect from './components/MissionSelect';
import StoryIntro from './components/StoryIntro';
import SoukMap from './components/SoukMap';
import MerchantShop from './components/MerchantShop';
import BargainGame from './components/BargainGame';
import Cart from './components/Cart';
import MissionComplete from './components/MissionComplete';
import WalletModal from './components/WalletModal';
import DashboardModal from './components/DashboardModal';
import DressUpAvatar from './DressUpAvatar';
import FreeMarketSouk from './components/FreeMarketSouk';
import FreeMarketShopping from './components/FreeMarketShopping';
import ClosetModal from './components/ClosetModal';
import MarketplaceScreen from './components/MarketplaceScreen';
import VocabLog from './components/VocabLog';

// Hooks
import useApi from './hooks/useApi';
import useTimer from './hooks/useTimer';
import useSounds from './hooks/useSounds';

// VERSION: 3.0 - Feb 2026 - Complete Game Redesign (7 Phases)

class GameErrorBoundary extends React.Component {
  state = { hasError: false, error: null };
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(e, info) { console.error('[GameError]', e, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-[#F5F0E8] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-lg p-6 max-w-sm text-center">
            <span className="text-4xl block mb-3">⚠️</span>
            <p className="text-red-600 text-sm font-medium mb-1">Something went wrong</p>
            <p className="text-red-500 text-xs mb-4 break-all">{this.state.error?.message}</p>
            <button onClick={this.props.onBack} className="px-5 py-2.5 bg-gray-200 rounded-xl font-semibold text-sm hover:bg-gray-300 transition-colors">Go Back</button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function SoukClothesGame({ API, currentUser, hasPaidPlan, onBack }) {
  const api = useApi(API);
  const { playSound } = useSounds();

  // ── Game flow state ──
  const [gameStep, setGameStep] = useState('welcome');
  const [loading, setLoading] = useState(true);

  // ── Data from backend ──
  const [items, setItems] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [missions, setMissions] = useState([]);
  const [areas, setAreas] = useState([]);
  const [gameSettings, setGameSettings] = useState({});

  // ── User state (persisted) ──
  const [coins, setCoins] = useState(0);
  const [ownedItems, setOwnedItems] = useState([]);
  const [completedOutfits, setCompletedOutfits] = useState([]);
  const [missionStars, setMissionStars] = useState({});
  const [totalStars, setTotalStars] = useState(0);
  const [unlockedAreas, setUnlockedAreas] = useState(['main-alley']);
  const [merchantFriendship, setMerchantFriendship] = useState({});
  const [loginStreak, setLoginStreak] = useState(0);
  const [bargainStats, setBargainStats] = useState({ total_bargains: 0, successful_walkways: 0, total_saved: 0 });

  // ── Session state ──
  const [currentMission, setCurrentMission] = useState(null);
  const [currentMerchant, setCurrentMerchant] = useState(null);
  const [bargainItem, setBargainItem] = useState(null);
  const [bargainMerchant, setBargainMerchant] = useState(null);
  const [cart, setCart] = useState([]);
  const [tokensSpent, setTokensSpent] = useState(0);
  const [darijaPhrasesUsed, setDarijaPhrasesUsed] = useState(0);
  const [missionResult, setMissionResult] = useState(null);

  // ── Free Market state ──
  const [gameMode, setGameMode] = useState(null); // 'free-market' | 'missions' | null
  const [closet, setCloset] = useState({ outfits: [], accessories: [] });
  const [showCloset, setShowCloset] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [wishlist, setWishlist] = useState([]);

  // ── Modal state ──
  const [showCart, setShowCart] = useState(false);
  const [showWallet, setShowWallet] = useState(false);
  const [showDashboard, setShowDashboard] = useState(false);
  const [showDressUp, setShowDressUp] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showVocab, setShowVocab] = useState(false);
  const [learnedWords, setLearnedWords] = useState([]);
  const [vocabWords, setVocabWords] = useState([]);
  const [dailyRewardAvailable, setDailyRewardAvailable] = useState(false);
  const [closetInitialView, setClosetInitialView] = useState('closet');
  const [marketplaceNotifCount, setMarketplaceNotifCount] = useState(0);

  // ── Daily mini-challenges for token earning ──
  const [dailyChallenges, setDailyChallenges] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('souk_daily_challenges') || '{}');
      const today = new Date().toDateString();
      if (saved.date === today) return saved;
      return { date: today, merchants_visited: 0, colors_learned: 0, bargains_won: 0, items_browsed: 0, claimed: {} };
    } catch { return { date: new Date().toDateString(), merchants_visited: 0, colors_learned: 0, bargains_won: 0, items_browsed: 0, claimed: {} }; }
  });

  // ── Ambient sound ──
  const [ambientOn, setAmbientOn] = useState(false);
  const ambientRef = useRef(null);

  useEffect(() => {
    const url = gameSettings.ambient_audio_url;
    if (!url) return;
    if (ambientOn) {
      if (!ambientRef.current || ambientRef.current.src !== url) {
        if (ambientRef.current) ambientRef.current.pause();
        ambientRef.current = new Audio(url);
        ambientRef.current.loop = true;
        ambientRef.current.volume = parseFloat(gameSettings.ambient_volume || 0.3);
      }
      ambientRef.current.play().catch(() => {});
    } else if (ambientRef.current) {
      ambientRef.current.pause();
    }
  }, [ambientOn, gameSettings.ambient_audio_url, gameSettings.ambient_volume]);

  // Cleanup ambient on unmount
  useEffect(() => {
    return () => { if (ambientRef.current) { ambientRef.current.pause(); ambientRef.current = null; } };
  }, []);

  // ── Lock body scroll on welcome page (mobile needs html + body + position:fixed) ──
  useEffect(() => {
    if (gameStep === 'welcome') {
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = '0';
    } else {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
    }
    return () => {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
    };
  }, [gameStep]);

  // ── Marketplace notification polling ──
  useEffect(() => {
    if (!currentUser?.id || !gameSettings.enable_p2p_trading) return;
    const checkNotifs = async () => {
      try {
        const data = await api.getUnreadCount(currentUser.id);
        if (data?.unread_count !== undefined) setMarketplaceNotifCount(data.unread_count);
      } catch {}
    };
    checkNotifs();
    const interval = setInterval(checkNotifs, 30000);
    return () => clearInterval(interval);
  }, [currentUser?.id, gameSettings.enable_p2p_trading, api]);

  // ── Timer ──
  const handleTimerExpire = useCallback(() => {
    playSound('error');
    if (currentMission && (gameStep === 'souk-map' || gameStep === 'merchant-shop')) {
      handleCompleteMission();
    }
  }, [currentMission, gameStep]); // eslint-disable-line

  const timer = useTimer(handleTimerExpire);

  // ── Load all game data ──
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [itemsData, merchantsData, missionsData, areasData, settingsData, vocabData] = await Promise.all([
          api.fetchItems(),
          api.fetchMerchants(),
          api.fetchOutfits(),
          api.fetchAreas(),
          api.fetchSettings(),
          api.fetchVocabWords().catch(() => []),
        ]);
        setItems(Array.isArray(itemsData) ? itemsData : []);
        setMerchants(Array.isArray(merchantsData) ? merchantsData : []);
        setMissions(Array.isArray(missionsData) ? missionsData : []);
        setAreas(Array.isArray(areasData) ? areasData : []);
        setGameSettings(settingsData || {});
        setVocabWords(Array.isArray(vocabData) ? vocabData : []);

        // Load user state
        if (currentUser?.id) {
          const state = await api.fetchGameState(currentUser.id);
          if (state) {
            setCoins(state.coins || 0);
            setOwnedItems(state.owned_items || []);
            setCompletedOutfits(state.completed_outfits || []);
            setMissionStars(state.mission_stars || {});
            setTotalStars(state.total_stars || 0);
            setUnlockedAreas(state.unlocked_areas || ['main-alley']);
            setMerchantFriendship(state.merchant_friendship || {});
            setLoginStreak(state.login_streak || 0);
            setBargainStats(state.bargain_stats || { total_bargains: 0, successful_walkways: 0, total_saved: 0 });
            setCloset(state.closet || { outfits: [], accessories: [] });
            setWishlist(state.wishlist || []);
            setLearnedWords(Array.isArray(state.learned_words) ? state.learned_words : []);
          }
          // Try claim daily reward
          try {
            const dailyRes = await api.claimDailyReward(currentUser.id);
            if (dailyRes.reward_claimed) {
              setCoins(dailyRes.new_balance);
              setLoginStreak(dailyRes.streak || 0);
              setDailyRewardAvailable(false);
            } else {
              setDailyRewardAvailable(false);
            }
          } catch {
            setDailyRewardAvailable(true);
          }
        }
      } catch (err) {
        console.error('Failed to load game data:', err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [currentUser?.id]); // eslint-disable-line

  // ── Save state to backend ──
  const saveState = useCallback(async (overrides = {}) => {
    if (!currentUser?.id) return;
    try {
      await api.saveGameState(currentUser.id, {
        coins: overrides.coins ?? coins,
        owned_items: overrides.ownedItems ?? ownedItems,
        completed_outfits: overrides.completedOutfits ?? completedOutfits,
        mission_stars: overrides.missionStars ?? missionStars,
        total_stars: overrides.totalStars ?? totalStars,
        unlocked_areas: overrides.unlockedAreas ?? unlockedAreas,
        merchant_friendship: overrides.merchantFriendship ?? merchantFriendship,
        bargain_stats: overrides.bargainStats ?? bargainStats,
        closet: overrides.closet ?? closet,
        wishlist: overrides.wishlist ?? wishlist,
        learned_words: overrides.learnedWords ?? learnedWords,
      });
    } catch (err) {
      console.error('Failed to save state:', err);
    }
  }, [currentUser?.id, coins, ownedItems, completedOutfits, missionStars, totalStars, unlockedAreas, merchantFriendship, bargainStats, closet, wishlist, learnedWords, api]);

  // ── Game Actions ──

  const handleSelectMission = (mission) => {
    console.log('[SoukGame] Selecting mission:', mission?.id, mission);
    setCurrentMission(mission);
    setCart([]);
    setTokensSpent(0);
    setDarijaPhrasesUsed(0);
    setMissionResult(null);
    setGameStep('story');
    playSound('tick');
  };

  const handleStartMission = () => {
    api.randomizeInventories();
    const timeLimit = currentMission.time_limit_seconds || gameSettings.default_time_limit || 480;
    timer.start(timeLimit);
    setGameStep('souk-map');
    playSound('tick');
  };

  const handleSelectMerchant = (merchant) => {
    setCurrentMerchant(merchant);
    setGameStep('merchant-shop');
    playSound('tick');
    updateChallenge('merchants_visited');
    // Increase friendship
    const fId = merchant.id;
    setMerchantFriendship(prev => ({
      ...prev,
      [fId]: Math.min(10, (prev[fId] || 0) + 1)
    }));
  };

  const handleBackToMap = () => {
    setCurrentMerchant(null);
    setGameStep('souk-map');
  };

  const handleStartBargain = (item, merchant) => {
    setBargainItem(item);
    setBargainMerchant(merchant);
    playSound('bargain');
  };

  const handleBargainComplete = ({ item, finalPrice, saved, phrasesUsed }) => {
    setBargainItem(null);
    setBargainMerchant(null);
    setBargainStats(prev => ({
      total_bargains: prev.total_bargains + 1,
      successful_walkways: prev.successful_walkways,
      total_saved: prev.total_saved + saved
    }));
    updateChallenge('bargains_won');

    // Both modes: add bargained item to cart
    setCart(prev => [...prev, { ...item, cartPrice: finalPrice }]);
    playSound('coin');
  };

  const handleAddToCart = (item) => {
    setCart(prev => [...prev, { ...item, cartPrice: item.base_price_tokens }]);
    playSound('coin');
  };

  const handleRemoveFromCart = (itemId) => {
    setCart(prev => prev.filter(c => c.id !== itemId));
    playSound('tick');
  };

  const handleCheckout = async () => {
    const total = cart.reduce((sum, c) => sum + (c.cartPrice || c.base_price_tokens || 0), 0);
    if (total > coins) return;

    const newCoins = coins - total;
    const newOwned = [...new Set([...ownedItems, ...cart.map(c => c.id)])];

    setCoins(newCoins);
    setOwnedItems(newOwned);
    setTokensSpent(prev => prev + total);
    setCart([]);
    setShowCart(false);

    // Auto-add purchased items to closet if space
    const maxO = gameSettings.closet_max_outfits || 8;
    const maxA = gameSettings.closet_max_accessories || 6;
    const newCloset = { outfits: [...(closet.outfits || [])], accessories: [...(closet.accessories || [])] };
    for (const cartItem of cart) {
      const lt = (cartItem.layer_type || cartItem.category || '').toLowerCase();
      const isAcc = lt === 'accessories' || lt === 'accessory';
      if (isAcc && newCloset.accessories.length < maxA && !newCloset.accessories.includes(cartItem.id)) {
        newCloset.accessories.push(cartItem.id);
      } else if (!isAcc && newCloset.outfits.length < maxO && !newCloset.outfits.includes(cartItem.id)) {
        newCloset.outfits.push(cartItem.id);
      }
    }
    setCloset(newCloset);

    await saveState({ coins: newCoins, ownedItems: newOwned, closet: newCloset });
    playSound('victory');

    // Check if mission can be completed
    if (currentMission) {
      const requiredItems = currentMission.required_items || [];
      const requiredIds = requiredItems.map(ri =>
        typeof ri === 'object' ? (ri.item_id || ri.id) : ri
      );
      const allFound = requiredIds.length === 0 || requiredIds.every(rid => newOwned.includes(rid));
      if (allFound && requiredIds.length > 0) {
        handleCompleteMission(newCoins, newOwned, total);
      }
    }
  };

  const handleCompleteMission = async (newCoins, newOwned, totalSpent) => {
    if (!currentMission || !currentUser?.id) return;
    timer.stop();

    try {
      const result = await api.completeMission(currentUser.id, {
        mission_id: currentMission.id,
        tokens_spent: totalSpent ?? tokensSpent,
        darija_phrases_used: darijaPhrasesUsed,
        items_purchased: cart.map(c => c.id),
        tokens_saved: bargainStats.total_saved
      });

      if (result.success) {
        setMissionResult(result);
        setCoins(result.new_balance);
        setTotalStars(result.total_stars);
        setMissionStars(prev => ({
          ...prev,
          [currentMission.id]: Math.max(prev[currentMission.id] || 0, result.stars)
        }));
        if (!completedOutfits.includes(currentMission.id)) {
          setCompletedOutfits(prev => [...prev, currentMission.id]);
        }
        if (result.newly_unlocked_areas?.length > 0) {
          setUnlockedAreas(prev => [...new Set([...prev, ...result.newly_unlocked_areas.map(a => a.id)])]);
        }
        setGameStep('mission-complete');
        playSound('victory');
      }
    } catch (err) {
      console.error('Failed to complete mission:', err);
    }
  };

  const handleDarijaUsed = () => {
    setDarijaPhrasesUsed(prev => prev + 1);
  };

  const handleTimeCost = (seconds) => {
    timer.deduct(seconds);
  };

  const handleReplay = () => {
    setCart([]);
    setTokensSpent(0);
    setDarijaPhrasesUsed(0);
    setMissionResult(null);
    const timeLimit = currentMission.time_limit_seconds || gameSettings.default_time_limit || 480;
    timer.start(timeLimit);
    api.randomizeInventories();
    setGameStep('souk-map');
    playSound('tick');
  };

  const handleBackToMissions = () => {
    setCurrentMission(null);
    setCurrentMerchant(null);
    setCart([]);
    timer.reset();
    setGameStep('mission-select');
  };

  const handleClaimDaily = async () => {
    if (!currentUser?.id) return;
    try {
      const res = await api.claimDailyReward(currentUser.id);
      if (res.reward_claimed) {
        setCoins(res.new_balance);
        setLoginStreak(res.streak || 0);
        setDailyRewardAvailable(false);
        playSound('coin');
      }
    } catch (err) {
      console.error('Failed to claim daily reward:', err);
    }
  };

  // ── Free Market Actions ──

  const handleFreeMarketMerchant = (merchant) => {
    setCurrentMerchant(merchant);
    setGameStep('free-market-shopping');
    playSound('tick');
    updateChallenge('merchants_visited');
    const fId = merchant.id;
    setMerchantFriendship(prev => ({
      ...prev,
      [fId]: Math.min(10, (prev[fId] || 0) + 1)
    }));
  };

  const handleFreeMarketPurchase = async (item, finalPrice) => {
    if (finalPrice > coins) return;
    const newCoins = coins - finalPrice;
    const newOwned = [...new Set([...ownedItems, item.id])];
    setCoins(newCoins);
    setOwnedItems(newOwned);

    // Auto-add to closet if space
    const maxOutfits = gameSettings.closet_max_outfits || 8;
    const maxAccessories = gameSettings.closet_max_accessories || 6;
    const newCloset = { outfits: [...(closet.outfits || [])], accessories: [...(closet.accessories || [])] };
    const lt = (item.layer_type || item.category || '').toLowerCase();
    const isAccessory = lt === 'accessories' || lt === 'accessory';
    if (isAccessory && newCloset.accessories.length < maxAccessories && !newCloset.accessories.includes(item.id)) {
      newCloset.accessories.push(item.id);
    } else if (!isAccessory && newCloset.outfits.length < maxOutfits && !newCloset.outfits.includes(item.id)) {
      newCloset.outfits.push(item.id);
    }
    setCloset(newCloset);

    await saveState({ coins: newCoins, ownedItems: newOwned, closet: newCloset });
    playSound('coin');
  };

  const handleUpdateCloset = async (newCloset) => {
    setCloset(newCloset);
    await saveState({ closet: newCloset });
  };

  const handleToggleWishlist = async (itemId) => {
    const newWishlist = wishlist.includes(itemId)
      ? wishlist.filter(id => id !== itemId)
      : [...wishlist, itemId];
    setWishlist(newWishlist);
    await saveState({ wishlist: newWishlist });
    playSound('tick');
  };

  // Challenge definitions (admin-configurable targets and rewards)
  const challengeDefs = [
    { key: 'merchants_visited', target: gameSettings.challenge_visit_target || 3, reward: gameSettings.challenge_visit_reward || 15, label: gameSettings.challenge_visit_label || 'Visit {N} merchants', icon: '🏪' },
    { key: 'colors_learned', target: gameSettings.challenge_colors_target || 5, reward: gameSettings.challenge_colors_reward || 10, label: gameSettings.challenge_colors_label || 'Learn {N} color words', icon: '🎨' },
    { key: 'bargains_won', target: gameSettings.challenge_bargain_target || 2, reward: gameSettings.challenge_bargain_reward || 20, label: gameSettings.challenge_bargain_label || 'Win {N} bargains', icon: '🤝' },
    { key: 'items_browsed', target: gameSettings.challenge_browse_target || 8, reward: gameSettings.challenge_browse_reward || 5, label: gameSettings.challenge_browse_label || 'Browse {N} items', icon: '👀' },
  ];

  const updateChallenge = useCallback((key, increment = 1) => {
    setDailyChallenges(prev => {
      const today = new Date().toDateString();
      const base = prev.date === today ? prev : { date: today, merchants_visited: 0, colors_learned: 0, bargains_won: 0, items_browsed: 0, claimed: {} };
      const updated = { ...base, [key]: (base[key] || 0) + increment };
      try { localStorage.setItem('souk_daily_challenges', JSON.stringify(updated)); } catch {}
      return updated;
    });
  }, []);

  const claimChallengeReward = useCallback(async (key, reward) => {
    if (!currentUser?.id) return;
    try {
      const result = await api.claimChallenge(currentUser.id, { challenge_key: key, reward_amount: reward });
      if (result.success) {
        setDailyChallenges(prev => {
          const updated = { ...prev, claimed: { ...prev.claimed, [key]: true } };
          try { localStorage.setItem('souk_daily_challenges', JSON.stringify(updated)); } catch {}
          return updated;
        });
        setCoins(result.new_balance);
        playSound('coin');
      }
    } catch (err) {
      console.error('[SoukClothesGame] Challenge claim failed:', err);
    }
  }, [currentUser?.id, api, playSound]);

  const addLearnedWord = useCallback((word, meaning, arabic, context, tab) => {
    setLearnedWords(prev => {
      if (prev.some(w => w.word === word && w.context === context)) return prev;
      // Track color challenge progress
      if (context === 'color') updateChallenge('colors_learned');
      const updated = [...prev, { word, meaning, arabic: arabic || '', context: context || 'other', tab: tab || 'missions', learnedAt: Date.now() }];
      // Persist to backend
      saveState({ learnedWords: updated });
      return updated;
    });
  }, [updateChallenge, saveState]);

  const handleSellItem = async (itemId) => {
    if (!currentUser?.id) return;
    try {
      const res = await api.sellItem(currentUser.id, { item_id: itemId });
      if (res.success) {
        setCoins(res.new_balance);
        setOwnedItems(prev => prev.filter(id => id !== itemId));
        // Remove from closet if equipped
        setCloset(prev => ({
          outfits: prev.outfits.filter(id => id !== itemId),
          accessories: prev.accessories.filter(id => id !== itemId),
        }));
        playSound('coin');
        return res;
      }
      return res;
    } catch (err) {
      console.error('Sell failed:', err);
      return { success: false };
    }
  };

  const handleTradeItem = async (oldItemId, newItem) => {
    try {
      const res = await api.tradeItem(currentUser.id, {
        old_item_id: oldItemId,
        new_item_id: newItem.id,
      });
      if (res.success) {
        setCoins(res.new_balance);
        setOwnedItems(prev => [...prev.filter(id => id !== oldItemId), newItem.id]);
        // Update closet: swap old for new
        setCloset(prev => ({
          outfits: prev.outfits.map(id => id === oldItemId ? newItem.id : id),
          accessories: prev.accessories.map(id => id === oldItemId ? newItem.id : id),
        }));
        playSound('coin');
      }
    } catch (err) {
      console.error('Trade failed:', err);
    }
  };

  const handleRefreshState = useCallback(async () => {
    if (!currentUser?.id) return;
    try {
      const state = await api.fetchGameState(currentUser.id);
      if (state) {
        setCoins(state.coins || 0);
        setOwnedItems(state.owned_items || []);
        setCloset(state.closet || { outfits: [], accessories: [] });
      }
    } catch (err) { console.error('Refresh state error:', err); }
  }, [currentUser?.id, api]);

  const handleGameBack = () => {
    if (gameStep === 'marketplace') {
      setGameStep('welcome');
    } else if (gameStep === 'free-market-shopping') {
      setCurrentMerchant(null);
      setGameStep('free-market-souk');
    } else if (gameStep === 'free-market-souk') {
      setGameMode(null);
      setGameStep('welcome');
    } else if (gameStep === 'merchant-shop') handleBackToMap();
    else if (gameStep === 'souk-map' || gameStep === 'story') handleBackToMissions();
    else if (gameStep === 'mission-select') { setGameMode(null); setGameStep('welcome'); }
    else if (gameStep === 'welcome') onBack?.();
    else if (gameStep === 'mission-complete') handleBackToMissions();
    else onBack?.();
  };

  // ── Loading screen ──
  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F0E8] flex items-center justify-center">
        <div className="text-center">
          <span className="text-5xl block mb-4 animate-walking">🏪</span>
          <p className="text-gray-600 font-medium">{gameSettings.welcome_loading_text || 'Loading the Souk...'}</p>
        </div>
      </div>
    );
  }

  // ── Dress Up overlay ──
  if (showDressUp) {
    return (
      <DressUpAvatar
        API={API}
        currentUser={currentUser}
        userId={currentUser?.id}
        onBack={() => setShowDressUp(false)}
        onClose={() => setShowDressUp(false)}
        gameSettings={gameSettings}
      />
    );
  }

  return (
    <div className={`${gameStep === 'welcome' ? 'h-[calc(100vh-10rem)] overflow-hidden' : 'min-h-screen'} bg-[#F5F0E8]`}>
      {/* Top Bar — show for souk-map, merchant-shop, and free-market steps */}
      {(gameStep === 'souk-map' || gameStep === 'merchant-shop') && (
        <TopBar
          coins={coins}
          timerDisplay={timer.isRunning ? timer.display : null}
          timerIsLow={timer.isLow}
          missionName={currentMission?.name_english || currentMission?.name}
          gameSettings={gameSettings}
          onBack={handleGameBack}
          gameStep={gameStep}
        />
      )}

      {/* Floating Menu — both modes get cart; free-market also gets closet & marketplace */}
      {(gameStep === 'souk-map' || gameStep === 'merchant-shop' ||
        gameStep === 'free-market-souk' || gameStep === 'free-market-shopping' ||
        gameStep === 'marketplace') && (
        <FloatingMenu
          cartCount={cart.length}
          vocabCount={learnedWords.length}
          marketplaceBadge={marketplaceNotifCount}
          onCart={() => { setShowCart(true); playSound('tick'); }}
          onCloset={gameMode === 'free-market' ? () => { setClosetInitialView('closet'); setShowCloset(true); playSound('tick'); } : null}
          onTrade={gameMode === 'free-market' && gameSettings.enable_p2p_trading === true ? () => { setClosetInitialView('trade-panel'); setShowCloset(true); playSound('tick'); } : null}
          tradeBadge={marketplaceNotifCount}
          onMarketplace={gameMode === 'free-market' && gameSettings.enable_p2p_trading === true ? () => { setGameStep('marketplace'); playSound('tick'); } : null}
          onWallet={() => { setShowWallet(true); playSound('tick'); }}
          onDashboard={() => { setShowDashboard(true); playSound('tick'); }}
          onDressUp={() => { setShowDressUp(true); playSound('tick'); }}
          onVocab={
            gameSettings.vocab_enabled_marketplace === false && gameSettings.vocab_enabled_missions === false
            ? null
            : () => { setShowVocab(true); playSound('tick'); }
          }
          ambientOn={ambientOn}
          onToggleAmbient={gameSettings.ambient_audio_url ? () => setAmbientOn(prev => !prev) : null}
          onHelp={() => { setShowHelp(true); playSound('tick'); }}
          settings={gameStep === 'marketplace' ? { ...gameSettings, fm_menu_mobile_size: 'small', fm_menu_desktop_size: 'small' } : gameSettings}
        />
      )}

      {/* ── Game Screens ── */}

      {gameStep === 'welcome' && (
        <div className="bg-[#F5F0E8] flex items-center justify-center p-4">
          <div className="max-w-md w-full">
            {/* Back button */}
            <button
              onClick={() => onBack?.()}
              className="flex items-center gap-1.5 text-gray-500 hover:text-gray-700 text-sm font-medium mb-4 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              {gameSettings.welcome_back_button || 'Back to Games'}
            </button>

            <div className="bg-white rounded-3xl shadow-lg border border-gray-100 overflow-hidden animate-fade-up">
              {/* Top accent */}
              <div className="h-2 bg-gradient-to-r from-[#C84B31] via-[#D4A843] to-[#2D6A4F]" />

              <div className="p-6 sm:p-8 text-center">
                {/* Title */}
                <span className="text-5xl block mb-3">{gameSettings.welcome_icon || '🏪'}</span>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">{gameSettings.welcome_title || 'Souk L7wayej'}</h1>
                <p className="text-sm text-gray-500 mb-6">{gameSettings.welcome_subtitle || 'The Moroccan Clothes Market Adventure'}</p>

                {/* How to Play */}
                <div className="bg-gray-50 rounded-xl p-4 text-left mb-4">
                  <h3 className="text-sm font-bold text-gray-800 mb-3">{gameSettings.welcome_how_to_play_heading || 'How to Play'}</h3>
                  <div className="space-y-2.5">
                    {(gameSettings.how_to_play || [
                      { title: 'Choose a mission', description: 'each has a budget, time limit, and required items' },
                      { title: 'Explore the souk', description: 'walk between areas and discover different merchants' },
                      { title: 'Bargain in Darija', description: 'use Darija phrases to negotiate prices with merchants' },
                      { title: 'Earn 3 stars', description: 'find all items, stay under budget, and use Darija' }
                    ]).map((step, i) => (
                      <div key={i} className="flex gap-2.5 items-start">
                        <span className={`text-sm flex-shrink-0 w-5 h-5 rounded-full text-white flex items-center justify-center text-[10px] font-bold mt-0.5 ${
                          ['bg-[#C84B31]', 'bg-[#D4A843]', 'bg-[#2D6A4F]', 'bg-amber-500', 'bg-blue-500', 'bg-purple-500'][i % 6]
                        }`}>{i + 1}</span>
                        <p className="text-xs text-gray-600"><span className="font-semibold text-gray-800">{step.title}</span> — {step.description}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Stats preview */}
                {totalStars > 0 && (
                  <div className="flex items-center justify-center gap-4 mb-4">
                    <div className="text-center">
                      <p className="text-lg font-bold text-amber-600">⭐ {totalStars}</p>
                      <p className="text-[10px] text-gray-400">{gameSettings.welcome_stats_stars_label || 'Stars'}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold text-amber-700">🪙 {coins}</p>
                      <p className="text-[10px] text-gray-400">{gameSettings.welcome_stats_tokens_label || 'Tokens'}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold text-[#2D6A4F]">{completedOutfits.length}</p>
                      <p className="text-[10px] text-gray-400">{gameSettings.welcome_stats_missions_label || 'Missions Done'}</p>
                    </div>
                  </div>
                )}

                {/* Mode selection buttons */}
                <div className="space-y-2.5 mb-3">
                  {gameSettings.fm_is_active !== false && (
                    <button
                      onClick={() => {
                        if (gameSettings.fm_is_pro_only && !hasPaidPlan) {
                          setShowUpgradeModal(true);
                          return;
                        }
                        setGameMode('free-market'); setGameStep('free-market-souk'); playSound('tick');
                      }}
                      className="w-full py-3.5 rounded-xl bg-[#2D6A4F] hover:bg-[#40916C] text-white font-bold text-base transition-colors flex items-center justify-center gap-2"
                    >
                      <span>🛍️</span> {gameSettings.welcome_fm_button || 'Free Market'}
                      {gameSettings.fm_is_pro_only && (
                        <span className="text-[9px] bg-amber-400 text-amber-900 px-1.5 py-0.5 rounded-full font-bold ml-1">
                          {hasPaidPlan ? '⭐ PRO' : '🔒 PRO'}
                        </span>
                      )}
                    </button>
                  )}
                  {gameSettings.missions_is_active !== false && (
                    <button
                      onClick={() => {
                        if (gameSettings.missions_is_pro_only && !hasPaidPlan) {
                          setShowUpgradeModal(true);
                          return;
                        }
                        setGameMode('missions'); setGameStep('mission-select'); playSound('tick');
                      }}
                      className="w-full py-3.5 rounded-xl bg-[#C84B31] hover:bg-[#a33d27] text-white font-bold text-base transition-colors flex items-center justify-center gap-2"
                    >
                      <span>🎯</span> {gameSettings.welcome_missions_button || 'Missions'}
                      {gameSettings.missions_is_pro_only && (
                        <span className="text-[9px] bg-amber-400 text-amber-900 px-1.5 py-0.5 rounded-full font-bold ml-1">
                          {hasPaidPlan ? '⭐ PRO' : '🔒 PRO'}
                        </span>
                      )}
                    </button>
                  )}
                </div>
                <div className="flex gap-4 justify-center mb-3">
                  {gameSettings.fm_is_active !== false && (
                    <p className="text-[10px] text-gray-400">{gameSettings.welcome_fm_desc || 'Shop, trade & dress up'}</p>
                  )}
                  {gameSettings.missions_is_active !== false && (
                    <p className="text-[10px] text-gray-400">{gameSettings.welcome_missions_desc || 'Complete outfit challenges'}</p>
                  )}
                </div>

                {/* Disclaimer */}
                <p className="text-[10px] text-gray-400 leading-relaxed">
                  {gameSettings.welcome_disclaimer || 'This is an educational game for learning Moroccan Darija. All merchants, products, and prices are simulated for learning purposes.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {gameStep === 'mission-select' && (
        <MissionSelect
          missions={missions}
          missionStars={missionStars}
          completedOutfits={completedOutfits}
          totalStars={totalStars}
          gameSettings={gameSettings}
          onSelectMission={handleSelectMission}
          onBack={() => setGameStep('welcome')}
        />
      )}

      {gameStep === 'story' && currentMission && (
        <GameErrorBoundary onBack={handleBackToMissions}>
          <StoryIntro
            mission={currentMission}
            items={items}
            coins={coins}
            gameSettings={gameSettings}
            onStart={handleStartMission}
            onBack={handleBackToMissions}
          />
        </GameErrorBoundary>
      )}

      {gameStep === 'souk-map' && (
        <SoukMap
          areas={areas}
          merchants={merchants}
          items={items}
          ownedItems={ownedItems}
          unlockedAreas={unlockedAreas}
          completedOutfits={completedOutfits}
          currentMission={currentMission}
          gameSettings={gameSettings}
          onSelectMerchant={handleSelectMerchant}
          onTimeCost={handleTimeCost}
          timerSeconds={timer.secondsLeft}
          walkingTimeCost={gameSettings.walking_time_cost || 30}
        />
      )}

      {gameStep === 'merchant-shop' && currentMerchant && (
        <MerchantShop
          merchant={currentMerchant}
          items={items}
          missionRequiredItems={currentMission?.required_items}
          ownedItems={ownedItems}
          cart={cart}
          coins={coins}
          budgetLimit={currentMission?.budget_limit || gameSettings.default_budget || 0}
          gameSettings={gameSettings}
          onBargain={handleStartBargain}
          onAddToCart={handleAddToCart}
          onBack={handleBackToMap}
          playSound={playSound}
        />
      )}

      {gameStep === 'mission-complete' && missionResult && (
        <MissionComplete
          mission={currentMission}
          result={missionResult}
          gameSettings={gameSettings}
          onReplay={handleReplay}
          onNextMission={handleBackToMissions}
          onBackToMap={handleBackToMissions}
          playSound={playSound}
        />
      )}

      {/* ── Free Market Screens ── */}

      {gameStep === 'free-market-souk' && (
        <FreeMarketSouk
          merchants={merchants}
          items={items}
          coins={coins}
          gameSettings={gameSettings}
          playerAvatar={currentUser?.avatar}
          ownedItems={ownedItems}
          merchantFriendship={merchantFriendship}
          wishlist={wishlist}
          onSelectMerchant={handleFreeMarketMerchant}
          onOpenCloset={() => { setClosetInitialView('closet'); setShowCloset(true); playSound('tick'); }}
          onDressUp={() => { setShowDressUp(true); playSound('tick'); }}
          onBack={() => { setGameMode(null); setGameStep('welcome'); }}
          playSound={playSound}
        />
      )}

      {gameStep === 'free-market-shopping' && currentMerchant && (
        <FreeMarketShopping
          merchant={currentMerchant}
          items={items}
          ownedItems={ownedItems}
          coins={coins}
          gameSettings={gameSettings}
          wishlist={wishlist}
          onBargain={handleStartBargain}
          onPurchase={handleFreeMarketPurchase}
          onAddToCart={handleAddToCart}
          onToggleWishlist={handleToggleWishlist}
          onOpenCloset={() => { setClosetInitialView('closet'); setShowCloset(true); playSound('tick'); }}
          onLearnWord={(w, m, a, ctx) => addLearnedWord(w, m, a, ctx, 'marketplace')}
          onBack={() => { setCurrentMerchant(null); setGameStep('free-market-souk'); }}
          onExitGame={() => { setCurrentMerchant(null); setGameMode(null); setGameStep('welcome'); }}
          playSound={playSound}
        />
      )}

      {/* ── Marketplace Full Screen ── */}

      {gameStep === 'marketplace' && (
        <MarketplaceScreen
          ownedItems={ownedItems}
          allItems={items}
          coins={coins}
          API={API}
          userId={currentUser?.id}
          gameSettings={gameSettings}
          onBack={() => setGameStep('welcome')}
          onRefreshState={handleRefreshState}
          onLearnWord={(w, m, a, ctx) => addLearnedWord(w, m, a, ctx, 'marketplace')}
          playSound={playSound}
        />
      )}

      {/* ── Modals ── */}

      {bargainItem && bargainMerchant && (
        <BargainGame
          item={bargainItem}
          merchant={bargainMerchant}
          gameSettings={gameSettings}
          onComplete={handleBargainComplete}
          onCancel={() => { setBargainItem(null); setBargainMerchant(null); }}
          onDarijaUsed={handleDarijaUsed}
          onLearnWord={(w, m, a, ctx) => addLearnedWord(w, m, a, ctx, gameMode === 'free-market' ? 'marketplace' : 'missions')}
          playSound={playSound}
        />
      )}

      {showCart && (
        <Cart
          cart={cart}
          coins={coins}
          budgetLimit={currentMission?.budget_limit || gameSettings.default_budget || 0}
          gameSettings={gameSettings}
          onRemove={handleRemoveFromCart}
          onCheckout={handleCheckout}
          onClose={() => setShowCart(false)}
        />
      )}

      {showWallet && (
        <WalletModal
          coins={coins}
          loginStreak={loginStreak}
          dailyRewardAvailable={dailyRewardAvailable}
          gameSettings={gameSettings}
          dailyChallenges={dailyChallenges}
          challengeDefs={challengeDefs}
          onClaimChallenge={claimChallengeReward}
          onClaimDaily={handleClaimDaily}
          onClose={() => setShowWallet(false)}
        />
      )}

      {showDashboard && (
        <DashboardModal
          totalStars={totalStars}
          totalTokensSaved={bargainStats.total_saved}
          wardrobe={ownedItems}
          loginStreak={loginStreak}
          missionStars={missionStars}
          gameSettings={gameSettings}
          onClose={() => setShowDashboard(false)}
        />
      )}

      {showCloset && (
        <ClosetModal
          ownedItems={ownedItems}
          allItems={items}
          closet={closet}
          maxOutfits={gameSettings.closet_max_outfits || 8}
          maxAccessories={gameSettings.closet_max_accessories || 6}
          gameSettings={gameSettings}
          API={API}
          userId={currentUser?.id}
          coins={coins}
          initialView={closetInitialView}
          onUpdateCloset={handleUpdateCloset}
          onTrade={handleTradeItem}
          onSellItem={handleSellItem}
          onDressUp={() => { setShowCloset(false); setShowDressUp(true); }}
          onClose={() => { setShowCloset(false); setClosetInitialView('closet'); }}
          playSound={playSound}
        />
      )}

      {/* Upgrade Modal — shown when free user clicks a PRO mode */}
      {showUpgradeModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-3 z-[60] animate-modal-overlay">
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full animate-modal-content overflow-hidden">
            <div className="h-1.5 bg-gradient-to-r from-amber-400 via-yellow-400 to-orange-500" />
            <div className="p-6 text-center">
              <span className="text-5xl block mb-3">🔒</span>
              <h3 className="text-xl font-bold text-gray-900 mb-2">{gameSettings.upgrade_title || 'Pro Feature'}</h3>
              <p className="text-sm text-gray-500 mb-6">
                {gameSettings.upgrade_message || 'This game mode is exclusive to Pro members. Upgrade your membership to unlock all premium features!'}
              </p>
              <div className="space-y-2.5">
                <button
                  onClick={() => { setShowUpgradeModal(false); window.location.href = '/membership'; }}
                  className="w-full py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold text-sm transition-all shadow-md"
                >
                  {gameSettings.upgrade_button || 'Upgrade to Pro'}
                </button>
                <button
                  onClick={() => setShowUpgradeModal(false)}
                  className="w-full py-2.5 rounded-xl border-2 border-gray-200 text-gray-500 font-semibold text-sm hover:border-gray-300 transition-colors"
                >
                  {gameSettings.upgrade_cancel || 'Maybe Later'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showVocab && (
        <VocabLog
          words={learnedWords}
          vocabWords={vocabWords}
          onClose={() => setShowVocab(false)}
          gameSettings={gameSettings}
          gameMode={gameMode}
        />
      )}

      {showHelp && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-3 z-[60] animate-modal-overlay">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[85vh] flex flex-col animate-modal-content">
            <div className="p-5 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
              <h3 className="font-bold text-gray-900 text-lg">{gameSettings.welcome_how_to_play_heading || 'How to Play'}</h3>
              <button onClick={() => setShowHelp(false)} className="text-gray-400 hover:text-gray-600 text-lg">✕</button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              <div className="space-y-3 text-sm text-gray-700">
                {(gameSettings.how_to_play || [
                  { title: 'Choose a mission', description: 'each has a budget, time limit, and required items' },
                  { title: 'Explore the souk', description: 'walk between areas and discover different merchants' },
                  { title: 'Bargain in Darija', description: 'use Darija phrases to negotiate prices with merchants' },
                  { title: 'Earn 3 stars', description: 'find all items, stay under budget, and use Darija' }
                ]).map((step, i) => (
                  <div key={i} className="flex gap-3 items-start">
                    <span className={`text-sm flex-shrink-0 w-6 h-6 rounded-full text-white flex items-center justify-center text-xs font-bold ${
                      ['bg-[#C84B31]', 'bg-[#D4A843]', 'bg-[#2D6A4F]', 'bg-amber-500', 'bg-blue-500', 'bg-purple-500'][i % 6]
                    }`}>{i + 1}</span>
                    <div>
                      <p className="font-semibold text-gray-900">{step.title}</p>
                      <p className="text-xs text-gray-500">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                <p className="text-xs text-blue-700">
                  {gameSettings.welcome_disclaimer || 'This is an educational game for learning Moroccan Darija. All merchants, products, and prices are simulated.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
